import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useOptionalPreferences } from '../contexts/PreferencesContext';
import TagsContext from '../contexts/TagsContext';
import { readActiveTag } from '../utils/activeTag';
import { normalizeId } from '../utils/ids';

// setTimeout's ceiling: an end further off than ~24.8 days is re-read by the
// next render or sign-in instead of a timer.
const MAX_TIMER_MS = 2147483647;

/**
 * THE ACTIVE TAG (docs/plans/tags-active-tag-proposal.md; build calls
 * b46–b52): which of my private tags my own forms put on every new item,
 * derived — no context of its own. The preference lives on the server
 * (`PreferencesContext`, `prefs.activeTag`), the tag's name, colour and icon
 * in my private tags (`TagsContext`), and this joins them:
 *
 *   { activeTag: { tagId, since, until, tag } | null, ready,
 *     isActive(tagId), makeActive(tagId, { until }), stop() }
 *
 * - OFF unless the stored tag is one of my private tags as this session knows
 *   them (a tag deleted in another tab reads as off; the API has cleared it)
 *   and its end, if it has one, is still ahead. A timer turns it off at the
 *   end while a page stays open.
 * - `ready` says the answer is final — the preferences have loaded, and the
 *   private tags too when one is set — so a new-item form knows when to put
 *   it on (once) and never guesses before.
 * - LAZY about the tags: my private tags are asked for only when a tag is
 *   active (the shell mounts this on every page; most people have none on).
 * - `makeActive` / `stop` write through `savePrefs` and throw a refusal back
 *   to the caller, which says it (utils/activeTag.js activeTagRefusal).
 */
export default function useActiveTag() {
	const prefsCtx = useOptionalPreferences();
	const tagsCtx = useContext(TagsContext);
	const raw = prefsCtx?.prefs?.activeTag || null;
	const [now, setNow] = useState(() => Date.now());

	const stored = useMemo(() => readActiveTag(raw, { now }), [raw, now]);

	// Off at its end, while the page stays open.
	const until = stored?.until || null;
	useEffect(() => {
		if (!until) return undefined;
		const ms = new Date(until).getTime() - Date.now();
		if (!(ms > 0) || ms > MAX_TIMER_MS) return undefined;
		const timer = setTimeout(() => setNow(Date.now()), ms + 50);
		return () => clearTimeout(timer);
	}, [until]);

	const storedTagId = stored?.tagId || null;
	const ensureTags = tagsCtx?.ensureLoaded;
	useEffect(() => {
		if (storedTagId && ensureTags) ensureTags();
	}, [storedTagId, ensureTags]);

	const tag = storedTagId && tagsCtx?.tagsById ? tagsCtx.tagsById.get(storedTagId) || null : null;
	const prefsReady = prefsCtx ? !prefsCtx.loading : true;
	const tagsReady = !storedTagId || !tagsCtx || tagsCtx.loaded || Boolean(tagsCtx.error && !tagsCtx.loading);
	const ready = prefsReady && tagsReady;

	const activeTag = useMemo(
		() => (stored && tag ? { ...stored, tag } : null),
		[stored, tag]
	);

	const savePrefs = prefsCtx?.savePrefs;
	const makeActive = useCallback(async (tagId, { until: end = null } = {}) => {
		if (!savePrefs) throw new Error('Preferences are not available here.');
		return savePrefs({ activeTag: { tagId: normalizeId(tagId), ...(end ? { until: end } : {}) } });
	}, [savePrefs]);
	const stop = useCallback(async () => {
		if (!savePrefs) throw new Error('Preferences are not available here.');
		return savePrefs({ activeTag: null });
	}, [savePrefs]);

	const activeId = activeTag?.tagId || null;
	const isActive = useCallback(tagId => Boolean(activeId) && (normalizeId(tagId) || '').toLowerCase() === activeId, [activeId]);

	return { activeTag, ready, isActive, makeActive, stop };
}
